import { Button } from '@lms/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@lms/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@lms/ui/dropdown-menu';
import { EllipsisVerticalIcon, TrashIcon } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslations } from 'use-intl';

interface DeleteConfirmationMenuProps<T extends number | string = number> {
  /** The ID to pass to the delete action */
  id: T;
  /** The delete action - receives the id and should return a Promise */
  onDelete: (id: T) => Promise<unknown>;
  /** Optional warning text shown in the dialog below the title */
  warningText?: string;
  /** Called after a successful delete (e.g. to SWR-mutate the list) */
  onDeleted?: () => void;
  /** Additional menu items to render before the delete option */
  children?: ReactNode;
}

/**
 * A dropdown menu with a delete option that shows a confirmation dialog.
 * Ported from the Next app; `router.refresh()` is replaced by the `onDeleted`
 * callback so callers can revalidate their SWR cache.
 */
export function DeleteConfirmationMenu<T extends number | string = number>({
  id,
  onDelete,
  warningText,
  onDeleted,
  children,
}: DeleteConfirmationMenuProps<T>) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const t = useTranslations('admin');

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(id);
      setWarningOpen(false);
      onDeleted?.();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={warningOpen} onOpenChange={setWarningOpen}>
        <DialogTrigger />
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('are_you_sure')}</DialogTitle>
          </DialogHeader>
          {warningText && <p>{warningText}</p>}
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </Button>
        </DialogContent>
      </Dialog>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger>
          <EllipsisVerticalIcon />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {children}
          <DropdownMenuItem
            className="text-destructive"
            onClick={() => setWarningOpen(true)}
          >
            <TrashIcon className="mr-2 inline-block h-4 w-4" />
            {t('remove', { what: '' }).trim()}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
